import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Clock, Car, Accessibility } from 'lucide-react';
import TitleWithLanterns from '../components/TitleWithLanterns';

const Location: React.FC = () => {
  const { t } = useTranslation();

  const schedule = [
    { day: t('hours.sunday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.monday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.tuesday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.wednesday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.thursday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.friday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') },
    { day: t('hours.saturday'), lunch: t('hours.lunch'), dinner: t('hours.dinner') }
  ];

  return (
    <div className="pt-20 animate-fadeIn">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden bg-white animate-fadeInUp animation-delay-200">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/location-hero-background.jpg)'
          }}
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40" />
        {/* Red gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/30 via-red-800/20 to-red-900/30" />
        {/* Bottom fade transition */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/50 to-transparent" />
        
        
        <TitleWithLanterns 
          title={t('location.title')}
          lanternType={3}
        />
      </section>

      {/* Location Info */}
      <section className="py-12 md:py-16 lg:py-20 bg-white relative animate-fadeInUp animation-delay-400">
        
        <div className="section-padding relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map */}
            <div>
              <div className="h-[500px] rounded-lg overflow-hidden shadow-xl">
                <iframe
                  src="https://maps.google.com/maps?q=4%20Rue%20Willy%20Bandt,%2025300%20Pontarlier,%20France&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Royal O'Nara Restaurant Location"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-elegant font-bold mb-8 text-gray-800">
                {t('location.practicalInfo')}
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="text-koi-red mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-lg">{t('location.address')}</p>
                    <p className="text-gray-600">{t('location.inMall')}</p>
                    <p className="text-gray-600">{t('location.aboveCastorama')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <Phone className="text-koi-red" size={24} />
                  <div>
                    <p className="font-semibold text-lg">{t('location.phone')}</p>
                    <a href="tel:0381895950" className="text-koi-red hover:underline">
                      03 81 89 59 50
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Car className="text-koi-red mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-lg">{t('location.parking')}</p>
                    <p className="text-gray-600">{t('info.parking')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Accessibility className="text-koi-red mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-lg">{t('location.accessibility')}</p>
                    <p className="text-gray-600">{t('info.pmr')}</p>
                  </div>
                </div>
              </div>

              <a
                href="https://www.google.com/maps?q=4+Rue+Willy+Bandt,+25300+Pontarlier,+France"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-8 bg-red-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-red-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 border-2 border-red-700"
              >
                {t('location.viewMap')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Opening Hours */}
      <section className="py-12 md:py-16 lg:py-20 bg-koi-light animate-fadeInUp animation-delay-600 relative">
        {/* Fade from white gradient at the top */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white to-transparent" />
        {/* Fade to white gradient at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
        <div className="section-padding">
          <div className="text-center mb-6 md:mb-8 lg:mb-12">
            <h2 className="text-4xl font-elegant font-bold mb-4 text-gray-800">
              {t('hours.title')}
            </h2>
            <div className="w-24 h-1 bg-koi-red mx-auto" />
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="grid grid-cols-3 bg-koi-red text-white font-semibold">
                <div className="p-4 text-center">{t('location.day')}</div>
                <div className="p-4 text-center border-l border-white/20">{t('location.lunch')}</div>
                <div className="p-4 text-center border-l border-white/20">{t('location.dinner')}</div>
              </div>
              {schedule.map((item, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  <div className="p-4 font-medium">{item.day}</div>
                  <div className="p-4 text-center text-gray-600">{item.lunch}</div>
                  <div className="p-4 text-center text-gray-600">{item.dinner}</div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <div className="flex items-center justify-center gap-2 text-lg">
                <Clock className="text-koi-red" size={20} />
                <span className="font-semibold">{t('info.open7days')}</span>
              </div>
              <p className="text-koi-red font-medium mt-4">{t('hours.note')}</p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Location;